import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Download, Upload, Loader2 } from "lucide-react";
import api from "../../lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl bg-slate-50 p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className="font-serif text-xl font-bold text-navy">{value}</div>
  </div>
);

// Slide-over with a single athlete's stats, sessions, purchases, feedback and controls.
export const UserDrawer = ({ userId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [credits, setCredits] = useState("");
  const load = useCallback(() => api.get(`/admin/users/${userId}/detail`).then(({ data }) => { setD(data); setCredits(String(data.user.credits ?? 0)); }), [userId]);
  useEffect(() => { load(); }, [load]);
  const saveCredits = async () => { await api.post(`/admin/users/${userId}/credits?credits=${parseInt(credits || "0", 10)}`); toast.success("Credits updated"); load(); onChanged?.(); };
  const sendReset = async () => {
    try { const { data } = await api.post(`/admin/users/${userId}/send-reset`); toast.success(`Reset link sent to ${data.sent_to}`); }
    catch (e) { toast.error("Could not send reset link"); }
  };
  const toggleActive = async () => {
    await api.post(`/admin/users/${userId}/${d.user.active === false ? "reactivate" : "deactivate"}`); toast.success("Updated"); load(); onChanged?.();
  };
  if (!d) return <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"><Loader2 className="animate-spin text-white" /></div>;
  const { user, stats, sessions, purchases, feedback } = d;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="admin-user-drawer">
      <div className="w-full max-w-md h-full bg-white overflow-y-auto mm-scroll p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-serif text-2xl font-bold text-navy" data-testid="drawer-user-name">{user.name}</div>
            <div className="text-sm text-slate-500">{user.email}</div>
            <div className="text-xs text-slate-500 capitalize mt-1">{user.profile?.sport} · {user.profile?.position} · {user.profile?.grad_year} · guide {user.profile?.guide?.replace("_", " ")}</div>
            <div className="text-xs text-slate-400 mt-0.5">Last active {user.last_active ? new Date(user.last_active).toLocaleString() : "—"} · {user.active === false ? "INACTIVE" : "active"}</div>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-5">
          <Stat label="Credits" value={user.credits ?? 0} testId="drawer-credits" />
          <Stat label="Completed" value={stats.sessions_completed} testId="drawer-completed" />
          <Stat label="Unlocked" value={stats.sessions_unlocked} testId="drawer-unlocked" />
          <Stat label="Streak" value={`${user.journal_streak || 0}d`} testId="drawer-streak" />
          <Stat label="Journals" value={stats.journals} testId="drawer-journals" />
          <Stat label="Check-ins" value={stats.checkins} testId="drawer-checkins" />
          <Stat label="Pending custom" value={stats.pending_custom} testId="drawer-pending" />
          <Stat label="Purchases" value={stats.purchases} testId="drawer-purchases" />
          <Stat label="Spend" value={`$${(stats.spend || 0).toFixed(2)}`} testId="drawer-spend" />
        </div>
        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <input data-testid="drawer-credits-input" type="number" min="0" value={credits} onChange={(e) => setCredits(e.target.value)}
            className="w-24 px-3 py-2 rounded-xl border border-slate-300 text-sm" />
          <button data-testid="drawer-save-credits" onClick={saveCredits} className="bg-royal text-white text-xs font-bold px-3 py-2.5 rounded-xl">Set credits</button>
          <button data-testid="drawer-send-reset" onClick={sendReset} className="text-xs font-bold px-3 py-2.5 rounded-xl bg-slate-100 text-navy">Send reset link</button>
          <button data-testid="drawer-toggle-active" onClick={toggleActive} className={`text-xs font-bold px-3 py-2.5 rounded-xl ${user.active === false ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
            {user.active === false ? "Reactivate" : "Deactivate"}
          </button>
        </div>
        <Section title={`Sessions (${sessions.length})`}>
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100">
              <div className="min-w-0"><div className="font-semibold text-navy truncate">{s.title}</div><div className="text-xs text-slate-500 capitalize">{s.kind} · {s.type} · {s.lane || s.category}</div></div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${!s.approved ? "bg-amber-100 text-amber-700" : s.completed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {!s.approved ? "pending" : s.completed ? "done" : "new"}
              </span>
            </div>
          ))}
        </Section>
        <Section title={`Purchases (${purchases.length})`}>
          {purchases.length === 0 && <div className="text-xs text-slate-400">No purchases yet.</div>}
          {purchases.map((p) => <div key={`${p.lookup_key}-${p.created_at}`} className="text-sm py-1 text-navy">{p.lookup_key} · ${(p.amount / 100).toFixed(2)} · {p.created_at?.slice(0, 10)}</div>)}
        </Section>
        <Section title={`Feedback (${feedback.length})`}>
          {feedback.length === 0 && <div className="text-xs text-slate-400">No feedback yet.</div>}
          {feedback.map((f) => <div key={`${f.content_id}-${f.at}`} className="text-sm py-1 text-navy">{f.rating} {f.note ? `· ${f.note}` : ""}</div>)}
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mt-6"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</div>{children}</div>
);

export const ExportUsersButton = () => {
  const dl = async () => {
    const { data } = await api.get("/admin/users/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = "mindmoves_users.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return <button data-testid="admin-export-users" onClick={dl} className="flex items-center gap-1.5 text-xs font-bold text-royal"><Download size={14} /> Export CSV</button>;
};

// Upload a finished MP3 into the general library with type/category/tags.
export const UploadForm = ({ onDone }) => {
  const [f, setF] = useState({ title: "", type: "meditation", category: "General", tags: "", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an audio or MP4 file");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/admin/content/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to general library"); setF({ title: "", type: "meditation", category: "General", tags: "", description: "" }); setFile(null); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm";
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-4 mb-4 space-y-2.5" data-testid="admin-upload-form">
      <div className="font-bold text-navy text-sm flex items-center gap-2"><Upload size={16} /> Add general content <span className="text-xs text-slate-400 font-normal">MP3, WAV, M4A or MP4 (audio is extracted)</span></div>
      <input data-testid="upload-title" required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} />
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="upload-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inp}>
          {["meditation", "affirmation", "frequency", "visualization", "sleep", "breathwork"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input data-testid="upload-category" placeholder="Category (e.g. Calm)" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp} />
      </div>
      <input data-testid="upload-tags" placeholder="Tags, comma separated (pregame, sleep, confidence)" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className={inp} />
      <input data-testid="upload-description" placeholder="Short description (shown as transcript)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} />
      <input data-testid="upload-file" type="file" accept="audio/*,video/mp4,video/quicktime,.mp3,.m4a,.wav,.mp4,.mov" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
      <button data-testid="upload-submit" disabled={busy} className="w-full bg-royal text-white font-bold py-2.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
        {busy && <Loader2 size={16} className="animate-spin" />} Upload & publish
      </button>
    </form>
  );
};
